/**
 * ListTiles — the counts across the top of a list (Decisions: Total, Manual,
 * Auto, High impact).
 *
 * A list sets which tiles it shows. A tile that names a filter is a button:
 * pressing it narrows the list to what it counts, pressing it again lets go.
 * A tile with no filter (the total) is a reading only, and pressing it shows
 * everything again.
 */
import type { ReactNode } from 'react';

export type ListTileTone = 'neutral' | 'quiet' | 'green' | 'mustard' | 'coral';

export interface ListTile {
  id: string;
  label: string;
  value: ReactNode;
  tone?: ListTileTone;
  /** False when the tile only reads (no filter behind it). Default true. */
  filters?: boolean;
  /** Small print under the label. */
  hint?: ReactNode;
}

const TONE_TEXT: Record<ListTileTone, string> = {
  neutral: 'text-[var(--text-pri)]',
  quiet: 'text-[var(--text-ter)]',
  green: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  mustard: 'text-[var(--mustard)]',
  coral: 'text-[var(--coral)]',
};

export function ListTiles({
  tiles,
  activeId = null,
  onSelect,
  className = '',
}: {
  tiles: ListTile[];
  activeId?: string | null;
  /** The tile pressed, or null to show everything again. */
  onSelect?: (id: string | null) => void;
  className?: string;
}) {
  const cols = tiles.length >= 4 ? 'md:grid-cols-4' : tiles.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
  return (
    <div className={`grid grid-cols-2 ${cols} gap-2 md:gap-3 ${className}`.trim()} data-testid="list-tiles">
      {tiles.map((t) => {
        const filters = t.filters !== false && Boolean(onSelect);
        const active = activeId === t.id;
        const body = (
          <>
            <div className={`text-xl md:text-2xl font-bold tabular-nums ${TONE_TEXT[t.tone ?? 'neutral']}`}>{t.value}</div>
            <div className="text-xs text-[var(--text-ter)] mt-0.5">{t.label}</div>
            {t.hint != null && <div className="hidden md:block text-[11px] text-[var(--text-dim)] mt-1">{t.hint}</div>}
          </>
        );
        const cls = `bg-[var(--bg-raised)] border p-3 md:p-4 text-left ${active ? 'border-[var(--slate)]' : 'border-[var(--rule)]'}`;
        if (!onSelect) return <div key={t.id} className={cls}>{body}</div>;
        return (
          <button
            key={t.id}
            type="button"
            className={`${cls} hover:bg-[var(--hover-tint)] transition-colors`}
            style={active ? { borderColor: 'var(--slate)' } : undefined}
            aria-pressed={filters ? active : undefined}
            title={filters ? (active ? 'Show everything again' : `Show only: ${t.label}`) : 'Show everything'}
            onClick={() => onSelect(filters && !active ? t.id : null)}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}
